/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { createContext, useContext, useRef, type ReactNode } from "react";
import {
  mergeProps,
  useButton,
  useDisclosure,
  useFocusRing,
  useHover,
} from "react-aria";
import {
  useDisclosureGroupState,
  useDisclosureState,
  type DisclosureGroupState,
} from "react-stately";

import { cssFns, useCss } from "@superweb/css";
import { icons, useTypo, useUiColors } from "@superweb/ui";

const DisclosureGroupStateContext = createContext<DisclosureGroupState | null>(
  null,
);

export const DisclosureGroup = ({
  onExpandedChange,
  allowsMultipleExpanded,
  defaultExpandedKeys,
  children,
}: {
  onExpandedChange: (keys: string[]) => void;
  allowsMultipleExpanded?: boolean;
  defaultExpandedKeys?: string[];
  children: ReactNode;
}) => {
  const state = useDisclosureGroupState({
    onExpandedChange: (keys) => {
      onExpandedChange([...keys].map(String));
    },
    allowsMultipleExpanded,
    defaultExpandedKeys,
  });

  return (
    <div
      css={{
        display: "grid",
      }}
    >
      <DisclosureGroupStateContext.Provider value={state}>
        {children}
      </DisclosureGroupStateContext.Provider>
    </div>
  );
};

export type DisclosureItemProps = {
  id: string;
  title: ReactNode;
  label?: string;
  isExpanded: boolean;
  children?: ReactNode;
  icon?: ReactNode;
  addonAfter?: ReactNode;
  size?: "s" | "m";
  disabled?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
};

export const DisclosureItem = ({
  id,
  title,
  label,
  isExpanded,
  children,
  icon,
  addonAfter,
  size = "s",
  disabled = false,
  onExpandedChange,
}: DisclosureItemProps) => {
  const uiColors = useUiColors();
  const typo = useTypo();
  const css = useCss();

  const groupState = useContext(DisclosureGroupStateContext);

  const hasChildren = Boolean(children);

  const isDisabled = disabled || !hasChildren;

  const state = useDisclosureState({
    isExpanded,
    onExpandedChange: () => {
      groupState?.toggleKey(id);
      onExpandedChange?.(!isExpanded);
    },
  });

  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const { buttonProps: triggerProps, panelProps } = useDisclosure(
    { isExpanded, isDisabled },
    state,
    panelRef,
  );
  const { buttonProps } = useButton(triggerProps, triggerRef);
  const { isFocusVisible, focusProps } = useFocusRing();
  const { hoverProps, isHovered } = useHover({ isDisabled });

  const ChevronIcon = isExpanded
    ? label
      ? icons.ChevronUpL
      : icons.ChevronUpM
    : label
      ? icons.ChevronDownL
      : icons.ChevronDownM;

  const minHeight = { s: "40px", m: "56px" }[size];

  return (
    <div
      css={{
        display: "grid",
        ...cssFns.border({ radius: "16px" }),
      }}
    >
      <button
        ref={triggerRef}
        {...mergeProps(buttonProps, focusProps)}
        css={{
          ...cssFns.padding("8px", "16px"),
          ...cssFns.border({ radius: "16px", style: "none" }),
          display: "grid",
          columnGap: "8px",
          gridTemplateColumns: [
            !!icon && "max-content",
            addonAfter ? "auto" : "1fr",
            hasChildren && "max-content",
            !!addonAfter && "1fr",
          ]
            .filter(Boolean)
            .join(" "),
          alignItems: "center",
          justifyItems: "start",
          textAlign: "start",
          minHeight,
          color: uiColors.text,
          backgroundColor: "initial",
          outlineColor: uiColors.text,
          outlineStyle: isFocusVisible ? "solid" : "none",
          outlineWidth: "2px",
          cursor: isDisabled ? "default" : "pointer",
        }}
      >
        {icon}
        <span
          css={{
            display: "inline-grid",
            rowGap: "2px",
            alignItems: "center",
          }}
        >
          {!!label && (
            <span
              css={{
                color: uiColors.textMinor,
                ...typo({
                  level: "body2",
                  density: "normal",
                  weight: "regular",
                }),
              }}
            >
              {label}
            </span>
          )}
          <span
            css={typo({
              level: "body2",
              density: "normal",
              weight: !!label ? "regular" : "medium",
            })}
          >
            {title}
          </span>
        </span>
        {hasChildren && (
          <ChevronIcon
            {...hoverProps}
            className={css({
              marginInlineStart: !!addonAfter ? "-8px" : undefined,
              ...cssFns.border({ radius: "10px" }),
              ...cssFns.padding("2px"),
              ...(isHovered && {
                backgroundColor: cssFns.blend(
                  cssFns.transparentize(uiColors.everBack, 1),
                  uiColors.hover,
                ),
              }),
            })}
          />
        )}
        {!!addonAfter && (
          <div
            css={{
              justifySelf: "end",
            }}
          >
            {addonAfter}
          </div>
        )}
      </button>
      <div ref={panelRef} {...panelProps}>
        {children}
      </div>
    </div>
  );
};
